import { Link, useParams } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { ARTICLES, getArticle, SITE } from "@/config/site";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { FinalCTA } from "@/components/blocks";
import NotFound from "@/pages/NotFound";

export function ConseilsList() {
  const crumbs = [{ label: "Conseils" }];
  return (
    <>
      <Seo
        title="Conseils anti-nuisibles — frelons, guêpes, rongeurs | FRELONS50"
        description="Conseils pédagogiques et prudents pour reconnaître un frelon asiatique, savoir où se trouvent les nids et adopter les bons réflexes en cas de découverte d'un nid dans la Manche."
        path="/conseils"
        jsonLd={breadcrumbSchema(crumbs)}
      />
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4"><Breadcrumbs items={crumbs} /></section>
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-8">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-black leading-[1.08]">Conseils & prévention</h1>
        <p className="mt-5 text-lg text-brand-text max-w-2xl leading-relaxed">
          Des repères simples et prudents pour agir en sécurité. En cas de doute, contactez-nous : ne tentez jamais de
          traiter un nid vous-même.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.05}>
              <Link to={`/conseils/${a.slug}`} data-testid={`conseil-card-${a.slug}`} className="group block h-full bg-brand-gray rounded-3xl overflow-hidden border border-transparent hover:border-brand-gold/40 hover:shadow-xl transition-all">
                <div className="relative h-44 overflow-hidden">
                  <img src={a.image} alt={a.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" width="600" height="400" />
                </div>
                <div className="p-6">
                  <h2 className="font-display text-lg font-bold text-brand-black leading-snug">{a.title}</h2>
                  <p className="mt-2 text-sm text-brand-text leading-relaxed">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand-black group-hover:gap-2 transition-all">Lire <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <FinalCTA />
    </>
  );
}

export function ConseilArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <NotFound />;
  const crumbs = [{ label: "Conseils", to: "/conseils" }, { label: article.title }];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    articleBody: article.body.join(" "),
    author: { "@type": "Organization", name: SITE.brand },
    publisher: { "@type": "Organization", name: SITE.brand },
  };

  return (
    <>
      <Seo title={`${article.title} | FRELONS50`} description={article.excerpt} path={`/conseils/${article.slug}`} jsonLd={[articleSchema, breadcrumbSchema(crumbs)]} />
      <article className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Breadcrumbs items={crumbs} />
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-brand-black leading-[1.1] mt-6">{article.title}</h1>
        <div className="mt-6 rounded-2xl overflow-hidden">
          <img src={article.image} alt={article.alt} className="w-full h-auto" width="900" height="500" />
        </div>
        <div className="mt-8 space-y-5">
          {article.body.map((p, i) => (
            <p key={i} className="text-brand-text text-lg leading-relaxed">{p}</p>
          ))}
        </div>
        <div className="mt-10 bg-brand-black text-white rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-display font-bold text-lg text-center sm:text-left">Un doute sur un nid ? Contactez-nous.</p>
          <div className="flex gap-3">
            <a href={SITE.phoneHref} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-gold text-brand-black font-bold text-sm"><Phone className="w-4 h-4" /> Appeler</a>
            <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-white/30 text-white font-bold text-sm">Formulaire</Link>
          </div>
        </div>
      </article>
    </>
  );
}
